import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { rfqApi, categoriesApi, settingsApi } from '../../services/apiService';
import authService from '../../services/authService';
import './RequirementModal.css';

const RequirementModal = ({ isOpen = true, onClose }) => {
  const { user, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    category: '',
    subCategory: '',
    details: '',
    companyName: '',
    fullName: '',
    phoneNumber: '',
    country: '',
    countryCode: '',
    email: '',
    password: '',
    rfqType: 'both',
    projectTitle: '',
    projectBudget: '',
    projectTimeline: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [publicOffer, setPublicOffer] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [subCategoryDropdownOpen, setSubCategoryDropdownOpen] = useState(false);
  const [rfqTypeDropdownOpen, setRfqTypeDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryObj, setSelectedCategoryObj] = useState(null);
  
  // Specialist categories (Sales + Project)
  const [salesCategories, setSalesCategories] = useState([]);
  const [projectCategories, setProjectCategories] = useState([]);

  const categoryRef = useRef(null);
  const subCategoryRef = useRef(null);
  const rfqTypeRef = useRef(null);

  const rfqTypeOptions = [
    { value: 'both', label: 'I am looking for Product and Professional both' },
    { value: 'product', label: 'I am looking for Product only' },
    { value: 'professional', label: 'I am looking for Professional only' }
  ];

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (categoryRef.current && !categoryRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
      if (subCategoryRef.current && !subCategoryRef.current.contains(e.target)) {
        setSubCategoryDropdownOpen(false);
      }
      if (rfqTypeRef.current && !rfqTypeRef.current.contains(e.target)) {
        setRfqTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const [prodCats, specCats] = await Promise.all([
          categoriesApi.getAll(),
          settingsApi.getSpecialistCategories()
        ]);
        setCategories(prodCats);
        setSalesCategories(specCats.salesCategories || []);
        setProjectCategories(specCats.projectCategories || []);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  // Pre-fill form if user is logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        companyName: user.companyName || '',
        fullName: user.email?.split('@')[0] || '',
        email: user.email || '',
        country: user.country || '',
        phoneNumber: user.phone || ''
      }));
    }
  }, [isAuthenticated, user]);

  // Get subcategories from selected category
  const isProfessionalMode = formData.rfqType === 'professional';
  
  // Category list changes based on rfqType
  const categoryList = isProfessionalMode
    ? salesCategories.map(sc => ({ id: sc.id, name: sc.name, subcategories: [] }))
    : categories;
  
  // Subcategory list changes based on rfqType  
  const subCategoryList = isProfessionalMode
    ? projectCategories.map(pc => ({ id: pc.id, name: pc.name }))
    : (selectedCategoryObj?.subcategories || []);

  const categoryPlaceholder = isProfessionalMode ? 'Select Sales Category' : 'Select Products Category';
  const subCategoryPlaceholder = isProfessionalMode
    ? 'Select Project Category'
    : (formData.category ? 'Sub Category' : 'Select Category First');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategorySelect = (categoryObj) => {
    setFormData(prev => ({ ...prev, category: categoryObj.name, subCategory: '' }));
    setSelectedCategoryObj(categoryObj);
    setCategoryDropdownOpen(false);
    setSubCategoryDropdownOpen(false);
  };

  const handleSubCategorySelect = (subCategoryObj) => {
    setFormData(prev => ({ ...prev, subCategory: subCategoryObj.name }));
    setSubCategoryDropdownOpen(false);
  };

  // Reset category/subcategory when rfqType changes
  const handleRfqTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      rfqType: value,
      category: '',
      subCategory: '',
      ...(value === 'product' ? { projectTitle: '', projectBudget: '', projectTimeline: '' } : {})
    }));
    setSelectedCategoryObj(null);
    setRfqTypeDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.category) {
      setError('Please select a category');
      return;
    }
    if (!formData.details) {
      setError('Please provide requirement details');
      return;
    }
    if (!formData.companyName) {
      setError('Company name is required');
      return;
    }
    if (!formData.fullName) {
      setError('Full name is required');
      return;
    }
    if (!formData.phoneNumber) {
      setError('Phone number is required');
      return;
    }
    if (!formData.country) {
      setError('Country is required');
      return;
    }
    if (!formData.email) {
      setError('Email is required');
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      return;
    }
    if (!agreedToTerms) {
      setError('Please agree to the Terms of Use & Privacy Policy');
      return;
    }

    try {
      setLoading(true);

      // Verify password
      try {
        const verifyResult = await authService.verifyPassword(formData.email, formData.password);
        if (!verifyResult.valid) {
          setError('Incorrect password. Please enter the correct password.');
          setLoading(false);
          return;
        }
      } catch (verifyError) {
        console.error('Password verification error:', verifyError);
        setError(verifyError.response?.data?.error || 'Incorrect password. Please enter the correct password.');
        setLoading(false);
        return;
      }

      const rfqData = {
        category: formData.category,
        subCategory: formData.subCategory || null,
        details: formData.details,
        companyName: formData.companyName,
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        country: formData.country,
        countryCode: formData.countryCode || null,
        email: formData.email,
        isPublicOffer: publicOffer,
        userId: user?.id || null,
        rfqType: formData.rfqType || 'both',
        projectTitle: formData.projectTitle || null,
        projectBudget: formData.projectBudget || null,
        projectTimeline: formData.projectTimeline || null
      };

      await rfqApi.create(rfqData);
      setSuccess(true);

      // Reset form after 2 seconds and close modal
      setTimeout(() => {
        setFormData({
          category: '',
          subCategory: '',
          details: '',
          companyName: user?.companyName || '',
          fullName: user?.email?.split('@')[0] || '',
          phoneNumber: user?.phone || '',
          country: user?.country || '',
          countryCode: '',
          email: user?.email || '',
          password: '',
          rfqType: 'both',
          projectTitle: '',
          projectBudget: '',
          projectTimeline: ''
        });
        setPublicOffer(false);
        setAgreedToTerms(false);
        setSuccess(false);
        setSelectedCategoryObj(null);
        onClose();
      }, 2000);

    } catch (err) {
      console.error('Error submitting RFQ:', err);
      setError(err.response?.data?.error || 'Failed to submit RFQ. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="requirement-modal-overlay" onClick={onClose}>
      <div className="requirement-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="requirement-modal-header">
          <h2 className="requirement-title">Post Your Requirement</h2>
        </div>

        {/* Success Message */}
        {success && (
          <div className="requirement-alert requirement-alert-success">
            ✓ RFQ submitted successfully! Redirecting...
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="requirement-alert requirement-alert-error">
            <span>{error}</span>
            <button type="button" className="alert-close-btn" onClick={() => setError(null)}>×</button>
          </div>
        )}

        <form className="requirement-form" onSubmit={handleSubmit}>
          <div className="form-content">
            <div className="form-fields">

              {/* RFQ Type Dropdown - Looking for Product / Professional / Both */}
              <div className="dropdown-field" ref={rfqTypeRef}>
                <div
                  className="dropdown-input"
                  onClick={() => setRfqTypeDropdownOpen(!rfqTypeDropdownOpen)}
                >
                  <span className="dropdown-value">
                    {rfqTypeOptions.find(opt => opt.value === formData.rfqType)?.label || 'I am looking for...'}
                  </span>
                  <svg
                    className={`caret-icon ${rfqTypeDropdownOpen ? 'rotated' : ''}`}
                    width="24" height="24" viewBox="0 0 24 24" fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M20.031 9.53104L12.531 17.031C12.4614 17.1008 12.3787 17.1561 12.2876 17.1938C12.1966 17.2316 12.099 17.251 12.0004 17.251C11.9019 17.251 11.8043 17.2316 11.7132 17.1938C11.6222 17.1561 11.5394 17.1008 11.4698 17.031L3.96979 9.53104C3.82906 9.39031 3.75 9.19944 3.75 9.00042C3.75 8.80139 3.82906 8.61052 3.96979 8.46979C4.11052 8.32906 4.30139 8.25 4.50042 8.25C4.69944 8.25 4.89031 8.32906 5.03104 8.46979L12.0004 15.4401L18.9698 8.46979C19.0395 8.40011 19.1222 8.34483 19.2132 8.30712C19.3043 8.26941 19.4019 8.25 19.5004 8.25C19.599 8.25 19.6965 8.26941 19.7876 8.30712C19.8786 8.34483 19.9614 8.40011 20.031 8.46979C20.1007 8.53947 20.156 8.6222 20.1937 8.71324C20.2314 8.80429 20.2508 8.90187 20.2508 9.00042C20.2508 9.09896 20.2314 9.19654 20.1937 9.28759C20.156 9.37863 20.1007 9.46136 20.031 9.53104Z" fill="#6D6D6D"/>
                  </svg>
                </div>
                {rfqTypeDropdownOpen && (
                  <div className="dropdown-menu">
                    {rfqTypeOptions.map((opt) => (
                      <div
                        key={opt.value}
                        className={`dropdown-item ${formData.rfqType === opt.value ? 'selected' : ''}`}
                        onClick={() => handleRfqTypeChange(opt.value)}
                      >
                        {opt.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Category Dropdown */}
              <div className="dropdown-field" ref={categoryRef}>
                <div
                  className="dropdown-input"
                  onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                >
                  <span className={formData.category ? 'dropdown-value' : 'dropdown-placeholder'}>
                    {formData.category || categoryPlaceholder}
                  </span>
                  <svg
                    className={`caret-icon ${categoryDropdownOpen ? 'rotated' : ''}`}
                    width="24" height="24" viewBox="0 0 24 24" fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M20.031 9.53104L12.531 17.031C12.4614 17.1008 12.3787 17.1561 12.2876 17.1938C12.1966 17.2316 12.099 17.251 12.0004 17.251C11.9019 17.251 11.8043 17.2316 11.7132 17.1938C11.6222 17.1561 11.5394 17.1008 11.4698 17.031L3.96979 9.53104C3.82906 9.39031 3.75 9.19944 3.75 9.00042C3.75 8.80139 3.82906 8.61052 3.96979 8.46979C4.11052 8.32906 4.30139 8.25 4.50042 8.25C4.69944 8.25 4.89031 8.32906 5.03104 8.46979L12.0004 15.4401L18.9698 8.46979C19.0395 8.40011 19.1222 8.34483 19.2132 8.30712C19.3043 8.26941 19.4019 8.25 19.5004 8.25C19.599 8.25 19.6965 8.26941 19.7876 8.30712C19.8786 8.34483 19.9614 8.40011 20.031 8.46979C20.1007 8.53947 20.156 8.6222 20.1937 8.71324C20.2314 8.80429 20.2508 8.90187 20.2508 9.00042C20.2508 9.09896 20.2314 9.19654 20.1937 9.28759C20.156 9.37863 20.1007 9.46136 20.031 9.53104Z" fill="#6D6D6D"/>
                  </svg>
                </div>
                {categoryDropdownOpen && (
                  <div className="dropdown-menu">
                    {categoryList.map((cat, index) => (
                      <div
                        key={cat.id || index}
                        className="dropdown-item"
                        onClick={() => handleCategorySelect(cat)}
                      >
                        {cat.name}
                      </div>
                    ))}
                    {categoryList.length === 0 && (
                      <div className="dropdown-item disabled">No categories available</div>
                    )}
                  </div>
                )}
              </div>

              {/* Sub Category Dropdown */}
              <div className={`dropdown-field ${!formData.category && !isProfessionalMode ? 'disabled' : ''}`} ref={subCategoryRef}>
                <div
                  className={`dropdown-input ${!formData.category && !isProfessionalMode ? 'disabled' : ''}`}
                  onClick={() => (formData.category || isProfessionalMode) && setSubCategoryDropdownOpen(!subCategoryDropdownOpen)}
                >
                  <span className={formData.subCategory ? 'dropdown-value' : 'dropdown-placeholder'}>
                    {formData.subCategory || subCategoryPlaceholder}
                  </span>
                  <svg
                    className={`caret-icon ${subCategoryDropdownOpen ? 'rotated' : ''}`}
                    width="24" height="24" viewBox="0 0 24 24" fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M20.031 9.53104L12.531 17.031C12.4614 17.1008 12.3787 17.1561 12.2876 17.1938C12.1966 17.2316 12.099 17.251 12.0004 17.251C11.9019 17.251 11.8043 17.2316 11.7132 17.1938C11.6222 17.1561 11.5394 17.1008 11.4698 17.031L3.96979 9.53104C3.82906 9.39031 3.75 9.19944 3.75 9.00042C3.75 8.80139 3.82906 8.61052 3.96979 8.46979C4.11052 8.32906 4.30139 8.25 4.50042 8.25C4.69944 8.25 4.89031 8.32906 5.03104 8.46979L12.0004 15.4401L18.9698 8.46979C19.0395 8.40011 19.1222 8.34483 19.2132 8.30712C19.3043 8.26941 19.4019 8.25 19.5004 8.25C19.599 8.25 19.6965 8.26941 19.7876 8.30712C19.8786 8.34483 19.9614 8.40011 20.031 8.46979C20.1007 8.53947 20.156 8.6222 20.1937 8.71324C20.2314 8.80429 20.2508 8.90187 20.2508 9.00042C20.2508 9.09896 20.2314 9.19654 20.1937 9.28759C20.156 9.37863 20.1007 9.46136 20.031 9.53104Z" fill="#6D6D6D"/>
                  </svg>
                </div>
                {subCategoryDropdownOpen && subCategoryList.length > 0 && (
                  <div className="dropdown-menu">
                    {subCategoryList.map((subCat, index) => (
                      <div
                        key={subCat.id || index}
                        className="dropdown-item"
                        onClick={() => handleSubCategorySelect(subCat)}
                      >
                        {subCat.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Write Details Textarea */}
              <div className="textarea-field">
                <textarea
                  name="details"
                  placeholder="Write Details"
                  value={formData.details}
                  onChange={handleInputChange}
                />
              </div>

              {/* Project Fields - shown when looking for professional or both */}
              {(formData.rfqType === 'professional' || formData.rfqType === 'both') && (
                <>
                  <div className="input-field">
                    <input
                      type="text"
                      name="projectTitle"
                      placeholder="Project Title (e.g. Villa Construction in Dubai)"
                      value={formData.projectTitle}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="input-row">
                    <div className="input-field half">
                      <input
                        type="text"
                        name="projectBudget"
                        placeholder="Estimated Budget"
                        value={formData.projectBudget}
                        onChange={handleInputChange}
                      />
                    </div>
                    <div className="input-field half">
                      <input
                        type="text"
                        name="projectTimeline"
                        placeholder="Timeline (e.g. 3 months)"
                        value={formData.projectTimeline}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Company Name */}
              <div className="input-field">
                <input
                  type="text"
                  name="companyName"
                  placeholder="Company Name"
                  value={formData.companyName}
                  onChange={handleInputChange}
                />
              </div>

              {/* Full Name + Phone Number - Side by Side */}
              <div className="input-row">
                <div className="input-field half">
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Full Name"
                    value={formData.fullName}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="input-field half">
                  <input
                    type="tel"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Country + Country Code - Side by Side */}
              <div className="input-row">
                <div className="input-field half">
                  <input
                    type="text"
                    name="country"
                    placeholder="Country"
                    value={formData.country}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="input-field half">
                  <input
                    type="text"
                    name="countryCode"
                    placeholder="Country Code"
                    value={formData.countryCode}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="input-field with-icon">
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={handleInputChange}
                />
                <svg className="field-icon" width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4.42312 2.83989C4.36549 2.77492 4.29553 2.72204 4.21732 2.68431C4.1391 2.64658 4.05417 2.62475 3.96745 2.62009C3.88074 2.61542 3.79396 2.62802 3.71214 2.65714C3.63033 2.68626 3.55511 2.73133 3.49084 2.78973C3.42657 2.84814 3.37454 2.91872 3.33775 2.99738C3.30096 3.07605 3.28015 3.16123 3.27652 3.248C3.2729 3.33476 3.28653 3.42139 3.31663 3.50284C3.34673 3.5843 3.3927 3.65898 3.45187 3.72254L5.03015 5.45914C2.05077 7.28762 0.769445 10.1062 0.712844 10.2342C0.67553 10.3181 0.65625 10.4089 0.65625 10.5008C0.65625 10.5926 0.67553 10.6835 0.712844 10.7674C0.741555 10.8322 1.43636 12.3727 2.98101 13.9174C5.03917 15.9747 7.63874 17.0625 10.5 17.0625C11.9705 17.0708 13.4261 16.7681 14.7714 16.1741L16.576 18.16C16.6337 18.225 16.7036 18.2779 16.7818 18.3156C16.8601 18.3533 16.945 18.3752 17.0317 18.3798C17.1184 18.3845 17.2052 18.3719 17.287 18.3428C17.3688 18.3137 17.4441 18.2686 17.5083 18.2102C17.5726 18.1518 17.6246 18.0812 17.6614 18.0025C17.6982 17.9239 17.719 17.8387 17.7226 17.7519C17.7263 17.6652 17.7126 17.5785 17.6825 17.4971C17.6524 17.4156 17.6065 17.3409 17.5473 17.2774L4.42312 2.83989ZM8.30566 9.06114L11.7239 12.8223C11.2091 13.0931 10.619 13.1852 10.0462 13.0843C9.47341 12.9834 8.95034 12.6951 8.55915 12.2646C8.16796 11.8342 7.9308 11.286 7.88492 10.7062C7.83904 10.1264 7.98703 9.54773 8.30566 9.06114ZM10.5 15.75C7.97507 15.75 5.76925 14.832 3.94323 13.0224C3.19373 12.2776 2.55627 11.4279 2.05077 10.5C2.4355 9.77891 3.66351 7.76094 5.93495 6.44926L7.41151 8.06938C6.83987 8.80151 6.54546 9.71216 6.58032 10.6404C6.61518 11.5686 6.97707 12.4546 7.60202 13.1418C8.22697 13.829 9.07474 14.2731 9.99549 14.3957C10.9162 14.5182 11.8507 14.3113 12.6336 13.8116L13.8419 15.1405C12.7755 15.5496 11.6422 15.7563 10.5 15.75ZM10.9922 7.92172C10.8212 7.88909 10.6701 7.78986 10.5723 7.64587C10.4745 7.50187 10.4378 7.32491 10.4705 7.15391C10.5031 6.98291 10.6023 6.83187 10.7463 6.73403C10.8903 6.63619 11.0673 6.59956 11.2383 6.63219C12.0747 6.79434 12.8362 7.22253 13.4094 7.85289C13.9825 8.48325 14.3365 9.282 14.4186 10.13C14.4348 10.3033 14.3815 10.4759 14.2705 10.6099C14.1594 10.7439 13.9996 10.8283 13.8264 10.8445C13.8059 10.8457 13.7853 10.8457 13.7648 10.8445C13.6008 10.8452 13.4425 10.7845 13.321 10.6742C13.1995 10.564 13.1238 10.4123 13.1086 10.2489C13.0533 9.6849 12.8175 9.15373 12.4362 8.73444C12.0549 8.31515 11.5485 8.03011 10.9922 7.92172ZM20.2847 10.7674C20.2502 10.8445 19.4192 12.6845 17.5481 14.3604C17.4842 14.4194 17.4091 14.4652 17.3273 14.495C17.2455 14.5248 17.1586 14.538 17.0717 14.5339C16.9847 14.5298 16.8994 14.5084 16.8208 14.471C16.7422 14.4336 16.6718 14.381 16.6137 14.3161C16.5557 14.2512 16.5111 14.1755 16.4826 14.0932C16.4541 14.0109 16.4423 13.9238 16.4478 13.8369C16.4533 13.7501 16.476 13.6651 16.5146 13.5871C16.5533 13.5091 16.6071 13.4396 16.6728 13.3825C17.5908 12.5578 18.3619 11.5831 18.9533 10.5C18.4467 9.57119 17.8078 8.72094 17.0567 7.97586C15.2307 6.16789 13.0249 5.24996 10.5 5.24996C9.96798 5.24931 9.43682 5.29239 8.91187 5.37875C8.8265 5.39385 8.73899 5.39188 8.65438 5.37297C8.56978 5.35405 8.48976 5.31856 8.41896 5.26854C8.34815 5.21852 8.28795 5.15497 8.24185 5.08156C8.19574 5.00814 8.16464 4.92632 8.15033 4.84082C8.13603 4.75531 8.1388 4.66782 8.1585 4.5834C8.17821 4.49897 8.21444 4.41929 8.26511 4.34895C8.31578 4.27861 8.37989 4.219 8.45373 4.17358C8.52757 4.12816 8.60967 4.09781 8.6953 4.0843C9.29181 3.98581 9.89541 3.9367 10.5 3.93746C13.3612 3.93746 15.9608 5.0252 18.019 7.08336C19.5636 8.62801 20.2584 10.1694 20.2871 10.2342C20.3245 10.3181 20.3437 10.4089 20.3437 10.5008C20.3437 10.5926 20.3245 10.6835 20.2871 10.7674H20.2847Z" fill="#6D6D6D"/>
                </svg>
              </div>

              {/* Password */}
              <div className="input-field with-icon">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                />
                <button
                  type="button"
                  className="eye-button"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4.42312 2.83989C4.36549 2.77492 4.29553 2.72204 4.21732 2.68431C4.1391 2.64658 4.05417 2.62475 3.96745 2.62009C3.88074 2.61542 3.79396 2.62802 3.71214 2.65714C3.63033 2.68626 3.55511 2.73133 3.49084 2.78973C3.42657 2.84814 3.37454 2.91872 3.33775 2.99738C3.30096 3.07605 3.28015 3.16123 3.27652 3.248C3.2729 3.33476 3.28653 3.42139 3.31663 3.50284C3.34673 3.5843 3.3927 3.65898 3.45187 3.72254L5.03015 5.45914C2.05077 7.28762 0.769445 10.1062 0.712844 10.2342C0.67553 10.3181 0.65625 10.4089 0.65625 10.5008C0.65625 10.5926 0.67553 10.6835 0.712844 10.7674C0.741555 10.8322 1.43636 12.3727 2.98101 13.9174C5.03917 15.9747 7.63874 17.0625 10.5 17.0625C11.9705 17.0708 13.4261 16.7681 14.7714 16.1741L16.576 18.16C16.6337 18.225 16.7036 18.2779 16.7818 18.3156C16.8601 18.3533 16.945 18.3752 17.0317 18.3798C17.1184 18.3845 17.2052 18.3719 17.287 18.3428C17.3688 18.3137 17.4441 18.2686 17.5083 18.2102C17.5726 18.1518 17.6246 18.0812 17.6614 18.0025C17.6982 17.9239 17.719 17.8387 17.7226 17.7519C17.7263 17.6652 17.7126 17.5785 17.6825 17.4971C17.6524 17.4156 17.6065 17.3409 17.5473 17.2774L4.42312 2.83989ZM8.30566 9.06114L11.7239 12.8223C11.2091 13.0931 10.619 13.1852 10.0462 13.0843C9.47341 12.9834 8.95034 12.6951 8.55915 12.2646C8.16796 11.8342 7.9308 11.286 7.88492 10.7062C7.83904 10.1264 7.98703 9.54773 8.30566 9.06114ZM10.5 15.75C7.97507 15.75 5.76925 14.832 3.94323 13.0224C3.19373 12.2776 2.55627 11.4279 2.05077 10.5C2.4355 9.77891 3.66351 7.76094 5.93495 6.44926L7.41151 8.06938C6.83987 8.80151 6.54546 9.71216 6.58032 10.6404C6.61518 11.5686 6.97707 12.4546 7.60202 13.1418C8.22697 13.829 9.07474 14.2731 9.99549 14.3957C10.9162 14.5182 11.8507 14.3113 12.6336 13.8116L13.8419 15.1405C12.7755 15.5496 11.6422 15.7563 10.5 15.75ZM10.9922 7.92172C10.8212 7.88909 10.6701 7.78986 10.5723 7.64587C10.4745 7.50187 10.4378 7.32491 10.4705 7.15391C10.5031 6.98291 10.6023 6.83187 10.7463 6.73403C10.8903 6.63619 11.0673 6.59956 11.2383 6.63219C12.0747 6.79434 12.8362 7.22253 13.4094 7.85289C13.9825 8.48325 14.3365 9.282 14.4186 10.13C14.4348 10.3033 14.3815 10.4759 14.2705 10.6099C14.1594 10.7439 13.9996 10.8283 13.8264 10.8445C13.8059 10.8457 13.7853 10.8457 13.7648 10.8445C13.6008 10.8452 13.4425 10.7845 13.321 10.6742C13.1995 10.564 13.1238 10.4123 13.1086 10.2489C13.0533 9.6849 12.8175 9.15373 12.4362 8.73444C12.0549 8.31515 11.5485 8.03011 10.9922 7.92172ZM20.2847 10.7674C20.2502 10.8445 19.4192 12.6845 17.5481 14.3604C17.4842 14.4194 17.4091 14.4652 17.3273 14.495C17.2455 14.5248 17.1586 14.538 17.0717 14.5339C16.9847 14.5298 16.8994 14.5084 16.8208 14.471C16.7422 14.4336 16.6718 14.381 16.6137 14.3161C16.5557 14.2512 16.5111 14.1755 16.4826 14.0932C16.4541 14.0109 16.4423 13.9238 16.4478 13.8369C16.4533 13.7501 16.476 13.6651 16.5146 13.5871C16.5533 13.5091 16.6071 13.4396 16.6728 13.3825C17.5908 12.5578 18.3619 11.5831 18.9533 10.5C18.4467 9.57119 17.8078 8.72094 17.0567 7.97586C15.2307 6.16789 13.0249 5.24996 10.5 5.24996C9.96798 5.24931 9.43682 5.29239 8.91187 5.37875C8.8265 5.39385 8.73899 5.39188 8.65438 5.37297C8.56978 5.35405 8.48976 5.31856 8.41896 5.26854C8.34815 5.21852 8.28795 5.15497 8.24185 5.08156C8.19574 5.00814 8.16464 4.92632 8.15033 4.84082C8.13603 4.75531 8.1388 4.66782 8.1585 4.5834C8.17821 4.49897 8.21444 4.41929 8.26511 4.34895C8.31578 4.27861 8.37989 4.219 8.45373 4.17358C8.52757 4.12816 8.60967 4.09781 8.6953 4.0843C9.29181 3.98581 9.89541 3.9367 10.5 3.93746C13.3612 3.93746 15.9608 5.0252 18.019 7.08336C19.5636 8.62801 20.2584 10.1694 20.2871 10.2342C20.3245 10.3181 20.3437 10.4089 20.3437 10.5008C20.3437 10.5926 20.3245 10.6835 20.2871 10.7674H20.2847Z" fill="#6D6D6D"/>
                  </svg>
                </button>
              </div>
            </div>

            {/* Checkboxes and Button Section */}
            <div className="checkbox-button-section">
              {/* Checkboxes */}
              <div className="checkbox-container">
                <label className="checkbox-row" htmlFor="publicOffer">
                  <input
                    type="checkbox"
                    id="publicOffer"
                    checked={publicOffer}
                    onChange={() => setPublicOffer(!publicOffer)}
                    className="checkbox-input"
                  />
                  <span className={`checkbox-custom ${publicOffer ? 'checked' : ''}`}>
                    {publicOffer && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                  <span className="checkbox-text">Show as Public Offer and Send to All Suppliers.</span>
                </label>

                <label className="checkbox-row" htmlFor="agreeTerms">
                  <input
                    type="checkbox"
                    id="agreeTerms"
                    checked={agreedToTerms}
                    onChange={() => setAgreedToTerms(!agreedToTerms)}
                    className="checkbox-input"
                  />
                  <span className={`checkbox-custom ${agreedToTerms ? 'checked' : ''}`}>
                    {agreedToTerms && (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.6667 3.5L5.25 9.91667L2.33333 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </span>
                  <span className="checkbox-text">
                    I agree with the <span className="link-text">Terms of use</span> &amp; <span className="link-text">Privacy Policy.</span>
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="submit-button"
                disabled={loading || success}
              >
                {loading ? 'Submitting...' : success ? '✓ Submitted!' : 'Send RFQ'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequirementModal;